import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { sortBy } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { LensTopicsExternal } from "../../api/Blueprints";
import { useGetLensTopics } from "../../api/BlueprintsApi";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { LensTopicsButtonAdd, LensTopicsModal } from "./LensTopicsModal";
import LensTopicsRemove from "./LensTopicsRemove";

type Props = {
  behaviorKitUUID: string;
};

function countLabel(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function LensTopicsSection({ behaviorKitUUID }: Props) {
  const { mutate: getLensTopics, data, isLoading } = useGetLensTopics();
  const [editing, setEditing] = useState<LensTopicsExternal | undefined>();

  const loadLensTopics = useCallback(() => {
    if (behaviorKitUUID) {
      getLensTopics(behaviorKitUUID);
    }
  }, [behaviorKitUUID, getLensTopics]);

  useEffect(() => {
    loadLensTopics();
  }, [loadLensTopics]);

  const sorted = sortBy(data, (item) => item.name.toLocaleLowerCase());

  return (
    <div className="p-2">
      {isLoading && <LoadingSpinner />}
      {sorted?.map((group) => (
        <div
          key={group.external_id}
          className="w-full flex items-center justify-center mt-1 rounded border p-2 text-sm bg-white"
        >
          <span className="font-bold truncate text-ellipsis max-w-xs flex-grow-0">
            {group.name}
          </span>
          <div className="w-96 ml-auto overflow-hidden text-sm">
            <div className="flex gap-4 items-center justify-center p-2 m-1">
              Included:
              <span className="rounded-md py-1 px-2 m-2 bg-green-100">
                {countLabel(
                  group.seeds?.length ?? 0,
                  "Topic Seed",
                  "Topic Seeds"
                )}
              </span>
              <span className="rounded-md py-1 px-2 m-2 bg-green-100">
                {countLabel(
                  group.topics?.length ?? 0,
                  "Lens - Topic",
                  "Lens - Topics"
                )}
              </span>
            </div>
          </div>
          <div className="flex gap-2 ml-2 text-gray-500 items-center">
            <PencilSquareIcon
              className="w-4 cursor-pointer"
              onClick={() => {
                setEditing(group);
              }}
            />
            <LensTopicsRemove
              onRemove={loadLensTopics}
              lensTopicsUUID={group.external_id}
            />
          </div>
          <LensTopicsModal
            showModal={editing?.external_id === group.external_id}
            close={() => setEditing(undefined)}
            behaviorKitUUID={behaviorKitUUID}
            initial={group}
            onSave={loadLensTopics}
          />
        </div>
      ))}
      <div className="py-2">
        <LensTopicsButtonAdd
          behaviorKitUUID={behaviorKitUUID}
          onSave={loadLensTopics}
        />
      </div>
    </div>
  );
}

export default LensTopicsSection;
